import { useEffect, useState } from 'react'
import { CheckCircleFilled, CloseOutlined, EyeOutlined, LoadingOutlined, RedoOutlined } from '@ant-design/icons'
import { Modal, Button, Divider, Progress, Spin, Alert } from 'gokwik-ui-kit'
import GenericAlert from '../customAlert'
import DocumentAnalysisData from '../../document-upload/OCRResult'
import { getDocumentDownloaded } from '@library/utilities/helpers/helper'
import { getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'

const DocsStatusModal = ({
    item,
    doc,
    ocrResult,
    setOcrResult,
    visible,
    onClose,
    uploadProgress,
    uploadedFileName,
    onShowSampleClick,
    loadingAI,
    openUploadModal,
    handlePreview,
    isUploading,
    setCurrentDoc,
    handleDelete,
    showLowScoreAlert,
    setShowLowScoreAlert,
    ocrApiError,
    handleImageChange,
    uploadedFile,
}) => {
    const [docUploaded, setDocUploaded] = useState(false)
    const userData = useSelector(getUserData)

    useEffect(() => {
        if (ocrResult?.[0]?.aiResponse?.validity_score === 'Low') {
            setShowLowScoreAlert(true)

            logEvent(
                `kyc_doc_upload_failed_${doc?.name}`,
                'click',
                'kyc',
                localStorage.getItem('email'),
                userData?.merchantDetails?.m_id,
                userData?.merchantDetails?.short_name,
                userData?.userDetails?.name,
                { errors: ocrResult?.[0]?.aiResponse },
            )
        } else {
            setShowLowScoreAlert(false)
        }
    }, [ocrResult])

    useEffect(() => {
        setDocUploaded(!isUploading)
    }, [isUploading])

    const handleModalClose = () => {
        if (!loadingAI) {
            setOcrResult([])
            setCurrentDoc(null)
            onClose()
        }
    }

    const handleClose = async () => {
        onClose()
        if (showLowScoreAlert || ocrApiError) {
            setShowLowScoreAlert(false)
            await handleDelete(doc)
        }
    }

    return (
        <Modal
            open={visible}
            onCancel={handleModalClose}
            width={500}
            closable={false}
            footer={null}
            wrapClassName='custom-modal'
            className='[&_.ant-modal-content]:p-0'
            maskClosable={false}
        >
            <div className='flex flex-col'>
                <div className='flex justify-between items-start border-b mx-4 mt-4'>
                    <h2 className='text-lg font-semibold text-black'>{doc.label}</h2>
                    <div className='flex flex-col gap-1'>
                        {doc?.show_preview_document && (
                            <a onClick={() => onShowSampleClick(item)} className='text-sm text-primary-500'>
                                View Sample Document
                            </a>
                        )}
                        {doc?.sample_download_url && (
                            <a
                                onClick={() => getDocumentDownloaded(doc?.sample_download_url, doc?.name)}
                                className='text-sm text-primary-500'
                            >
                                Download Template
                            </a>
                        )}
                    </div>
                </div>

                <Divider className='w-full' />

                <div className='flex flex-col items-start justify-between border border-solid border-[#b4b4b4] rounded-md bg-white mx-4 p-4'>
                    <div className='flex flex-row justify-between w-full'>
                        <div className='flex-1'>
                            <p className='text-base font-normal text-black'>
                                {docUploaded ? (
                                    <span>
                                        Document Uploaded{' '}
                                        {!loadingAI && !showLowScoreAlert && (
                                            <CheckCircleFilled className='text-[#17B26A]' />
                                        )}
                                    </span>
                                ) : (
                                    'Uploading'
                                )}
                            </p>
                            <p className='text-sm text-[#b4b4b4] max-w-64'>{uploadedFileName}</p>
                        </div>
                        <div>
                            {!docUploaded ? (
                                <button
                                    className='w-8 h-8 border border-solid border-[#b4b4b4] rounded-md flex items-center justify-center text-lg text-[#b4b4b4] bg-white'
                                    aria-label='Cancel upload'
                                >
                                    X
                                </button>
                            ) : (
                                <>
                                    <button
                                        className='border border-solid border-[#b4b4b4] rounded-md px-2 py-1 bg-white mr-2'
                                        aria-label='Cancel upload'
                                        onClick={() => {
                                            onClose()
                                            openUploadModal()
                                            logEvent(
                                                `kyc_doc_reupload_${doc.name}`,
                                                'click',
                                                'kyc',
                                                localStorage.getItem('email'),
                                                userData?.merchantDetails?.m_id,
                                                userData?.merchantDetails?.short_name,
                                                userData?.userDetails?.name,
                                                { ...ocrResult?.[0]?.aiResponse },
                                            )
                                        }}
                                    >
                                        <RedoOutlined className='mr-2' />
                                        Re-upload
                                    </button>
                                    <button
                                        className='border border-solid border-[#b4b4b4] rounded-md px-2 py-1 bg-white'
                                        aria-label='Cancel upload'
                                        onClick={() => handlePreview(doc?.name)}
                                    >
                                        <EyeOutlined />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className='mt-3 flex items-center gap-2 w-full'>
                        <Progress
                            percent={uploadProgress}
                            showInfo={false}
                            strokeColor='#1677FF'
                            trailColor='#F3F3F3'
                            strokeWidth={8}
                            className='w-full'
                        />
                        <span className='text-sm text-black'>{uploadProgress}%</span>
                    </div>
                </div>
            </div>
            {docUploaded && (
                <div className='flex flex-col'>
                    {loadingAI ? (
                        <Alert
                            message={<p className='text-xs text-blue-900 font-semibold'>Validating document...</p>}
                            type='info'
                            showIcon
                            className='flex w-auto py-1 px-2 mx-4 rounded-md mt-4'
                            icon={<LoadingOutlined className='text-xs' />}
                        />
                    ) : (
                        <>
                            {ocrApiError ? (
                                <GenericAlert
                                    type='error'
                                    showIcon
                                    ctaText='Re-submit'
                                    showCTA
                                    onClickCTA={() => {
                                        onClose()
                                        openUploadModal()
                                    }}
                                >
                                    <div className='ml-2'>
                                        Something went wrong please retry again{' '}
                                        <a
                                            onClick={() => {
                                                handleClose
                                                handleImageChange(uploadedFile, doc)
                                            }}
                                        >
                                            retry OCR check
                                        </a>
                                    </div>
                                </GenericAlert>
                            ) : showLowScoreAlert ? (
                                <GenericAlert
                                    type='error'
                                    showIcon
                                    ctaText='Re-submit'
                                    showCTA
                                    onClickCTA={() => {
                                        onClose()
                                        openUploadModal()
                                    }}
                                >
                                    <div>
                                        {ocrResult?.map((i, idx) => {
                                            return (
                                                i?.aiResponse && (
                                                    <DocumentAnalysisData
                                                        key={idx}
                                                        response={i?.aiResponse}
                                                        document={doc}
                                                    />
                                                )
                                            )
                                        })}
                                    </div>
                                </GenericAlert>
                            ) : (
                                <></>
                            )}
                        </>
                    )}
                </div>
            )}
            <div className='flex flex-col w-auto m-4'>
                <Button
                    block
                    variant='primary'
                    className='text-white font-medium rounded-md h-10 px-4 mb-2'
                    onClick={() => {
                        logEvent(
                            `kyc_doc_upload_success_${doc.name}`,
                            'click',
                            'kyc',
                            localStorage.getItem('email'),
                            userData?.merchantDetails?.m_id,
                            userData?.merchantDetails?.short_name,
                            userData?.userDetails?.name,
                            { ...ocrResult?.[0]?.aiResponse },
                        )
                        onClose()
                    }}
                    disabled={loadingAI || !docUploaded || showLowScoreAlert || ocrApiError}
                >
                    Done
                </Button>
                <Button
                    block
                    className='font-medium rounded-md h-10 px-4 mb-4'
                    onClick={async () => await handleClose()}
                    disabled={loadingAI || !docUploaded}
                >
                    <CloseOutlined /> Close
                </Button>
            </div>
        </Modal>
    )
}

export default DocsStatusModal
